import React, {useEffect, useMemo, useRef, useState} from "react";
import {flushSync} from "react-dom";
import {useSearchParams} from "react-router-dom";
import {motion, MotionConfig} from "framer-motion";
import {Search} from "lucide-react";
import './AllTheCSS/FirstSFG.css';
import './AllTheCSS/FirstSFGEditorial.css';
import './AllTheCSS/FirstSFGAlpine.css';
import {Category, Design, designs, Flyer, flyers, IntelItem, intelItems, photos, Priority, sectionCode} from "./FirstSFGData";
import FirstSFGCeremony from "./FirstSFGCeremony";
import FirstSFGShowcase from "./FirstSFGShowcase";
import FirstSFGDesignSwitcher from "./FirstSFGDesignSwitcher";
import FirstSFGPhotoStory from "./FirstSFGPhotoStory";
import {AlpineAltimeter, AlpineFieldLog, AlpineHero} from "./FirstSFGAlpine";

interface props {
    setPageSelector: React.Dispatch<React.SetStateAction<number>>
}

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
const pad = (n: number) => n.toString().padStart(2, '0')

// Military date-time group, e.g. 141530Z OCT 26
export const formatDTG = (date: Date) =>
    `${pad(date.getUTCDate())}${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}Z ${MONTHS[date.getUTCMonth()]} ${pad(date.getUTCFullYear() % 100)}`

const formatShortDate = (iso: string) => {
    const [year, month, day] = iso.split('-').map(Number)
    return `${pad(day)} ${MONTHS[month - 1]} ${year}`
}

const toLocalDate = (iso: string) => {
    const [year, month, day] = iso.split('-').map(Number)
    return new Date(year, month - 1, day)
}

const daysUntil = (iso: string, now: Date) => {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const days = Math.round((toLocalDate(iso).getTime() - today.getTime()) / 86400000)
    return days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `In ${days} days`
}

interface ViewTransitionHandle {
    ready: Promise<void>
    finished: Promise<void>
}

const DESIGN_KEY = 'sfg-design'
const isDesign = (value: string | null): value is Design => designs.some(option => option.id === value)

const readStoredDesign = (): Design | null => {
    try {
        const stored = window.localStorage.getItem(DESIGN_KEY)
        return isDesign(stored) ? stored : null
    } catch {
        return null
    }
}

const SECTIONS = [
    {id: 'sfg-board', label: 'Board'},
    {id: 'sfg-ceremony', label: 'Ceremony'},
    {id: 'sfg-flyers', label: 'Flyers'},
    {id: 'sfg-services', label: 'Services'},
]

const FILTERS: ('ALL' | Category)[] = ['ALL', 'ANNOUNCEMENT', 'CEREMONY', 'EVENT', 'FLYER', 'DOCUMENT']
const PRIORITY_RANK: Record<Priority, number> = {FLASH: 0, PRIORITY: 1, ROUTINE: 2}

const reveal = {
    initial: {opacity: 0, y: 24},
    whileInView: {opacity: 1, y: 0},
    viewport: {once: true, margin: '-80px'},
    transition: {duration: .45, ease: 'easeOut'},
}

const useNow = (intervalMs: number) => {
    const [now, setNow] = useState(() => new Date())
    useEffect(() => {
        const timer = window.setInterval(() => setNow(new Date()), intervalMs)
        return () => window.clearInterval(timer)
    }, [intervalMs])
    return now
}

const Typewriter = ({text}: {text: string}) => {
    const [count, setCount] = useState(0)
    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setCount(text.length)
            return
        }
        const timer = window.setInterval(() => {
            setCount(current => {
                if (current >= text.length) {
                    window.clearInterval(timer)
                    return current
                }
                return current + 1
            })
        }, 70)
        return () => window.clearInterval(timer)
    }, [text])
    return (
        <span className={'sfg-typewriter'} aria-label={text}>
            <span aria-hidden={true}>{text.slice(0, count)}</span>
            <span className={'sfg-caret'} aria-hidden={true}/>
        </span>
    )
}

const IntelCard = ({item, isOpen, onToggle, onDownload}: {item: IntelItem, isOpen: boolean, onToggle: () => void, onDownload: () => void}) => (
    <li className={`sfg-intel ${isOpen ? 'open' : ''}`}>
        <button className={'sfg-intel-head'} onClick={onToggle} aria-expanded={isOpen} aria-controls={`sfg-intel-${item.id}`}>
            <span className={`sfg-priority sfg-priority-${item.priority.toLowerCase()}`}>{item.priority}</span>
            <span className={'sfg-intel-meta'}>
                {item.category} · {formatShortDate(item.date)}{item.location ? ` · ${item.location}` : ''}
            </span>
            <span className={'sfg-intel-title'}>{item.title}</span>
            <span className={'sfg-intel-summary'}>{item.summary}</span>
            <span className={'sfg-intel-toggle'} aria-hidden={true}>{isOpen ? '−' : '+'}</span>
        </button>
        <div className={'sfg-intel-body'} id={`sfg-intel-${item.id}`} hidden={!isOpen}>
            <ul>
                {item.details.map(detail => <li key={detail}>{detail}</li>)}
            </ul>
            {item.category === 'DOCUMENT' &&
                <button className={'sfg-btn sfg-btn-ghost'} onClick={onDownload}>Download PDF</button>
            }
        </div>
    </li>
)

const FlyerPoster = ({flyer, large, design}: {flyer: Flyer, large?: boolean, design: Design}) => (
    <div className={`sfg-poster sfg-poster-${flyer.theme} ${large ? 'large' : ''}`}>
        {design === 'editorial' && <img className={'sfg-poster-photo'} src={flyer.photo} alt={''} loading={'lazy'}/>}
        <span className={'sfg-poster-mark'} aria-hidden={true}>{flyer.mark}</span>
        <span className={'sfg-poster-kicker'}>{flyer.kicker}</span>
        <span className={'sfg-poster-title'}>{flyer.title}</span>
        <span className={'sfg-poster-subtitle'}>{flyer.subtitle}</span>
        <span className={'sfg-poster-rule'} aria-hidden={true}/>
        <span className={'sfg-poster-date'}>{flyer.date}</span>
        <span className={'sfg-poster-location'}>{flyer.location}</span>
        {large &&
            <ul className={'sfg-poster-details'}>
                {flyer.details.map(detail => <li key={detail}>{detail}</li>)}
            </ul>
        }
    </div>
)

const FlyerModal = ({flyer, onClose, design}: {flyer: Flyer, onClose: () => void, design: Design}) => {
    const closeRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        const previousFocus = document.activeElement as HTMLElement | null
        closeRef.current?.focus()
        document.body.style.overflow = 'hidden'
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', handleKey)
        return () => {
            document.body.style.overflow = 'auto'
            window.removeEventListener('keydown', handleKey)
            previousFocus?.focus()
        }
    }, [onClose])

    return (
        <div className={'sfg-modal-backdrop'} onClick={onClose}>
            <div className={'sfg-modal'} role={'dialog'} aria-modal={true} aria-label={`${flyer.title} flyer`} onClick={event => event.stopPropagation()}>
                <button ref={closeRef} className={'sfg-modal-close'} onClick={onClose} aria-label={'Close flyer'}>✕</button>
                <FlyerPoster flyer={flyer} design={design} large/>
            </div>
        </div>
    )
}

const FirstSFG = ({setPageSelector}: props) => {
    const now = useNow(1000)
    const [filter, setFilter] = useState<'ALL' | Category>('ALL')
    const [query, setQuery] = useState('')
    const [openId, setOpenId] = useState<string | null>('cor')
    const [activeFlyer, setActiveFlyer] = useState<Flyer | null>(null)
    const [activeSection, setActiveSection] = useState('')
    const [toast, setToast] = useState('')
    const [searchParams, setSearchParams] = useSearchParams()
    const [design, setDesign] = useState<Design>(() => {
        const fromUrl = searchParams.get('design')
        return isDesign(fromUrl) ? fromUrl : readStoredDesign() ?? 'tactical'
    })
    const [isBriefBarVisible, setIsBriefBarVisible] = useState(false)
    const isTactical = design === 'tactical'
    const isEditorial = design === 'editorial'
    const isAlpine = design === 'alpine'

    const switchDesign = () => {
        const index = designs.findIndex(option => option.id === design)
        const next = designs[(index + 1) % designs.length].id
        const apply = () => flushSync(() => setDesign(next))
        const transitionDocument = document as Document & {startViewTransition?: (update: () => void) => ViewTransitionHandle}
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        if (transitionDocument.startViewTransition && !reduceMotion && document.visibilityState === 'visible') {
            const transition = transitionDocument.startViewTransition(apply)
            // The browser aborts the crossfade (and rejects these) if the tab is hidden mid-transition.
            // The design still switches, so the rejections are expected and safe to ignore.
            transition.ready.catch(() => undefined)
            transition.finished.catch(() => undefined)
        } else {
            apply()
        }
        setSearchParams({design: next}, {replace: true})
        try {
            window.localStorage.setItem(DESIGN_KEY, next)
        } catch {
            // Storage can be unavailable (private mode); the URL still carries the choice.
        }
        setToast(`Switched to the ${designs.find(option => option.id === next)?.name} design.`)
    }

    useEffect(() => {
        window.scrollTo({top: 0, behavior: 'auto'})
        document.title = '1st Special Forces Group (Airborne)'
        return () => {
            document.title = '75th Ranger Regiment'
        }
    }, [])

    useEffect(() => {
        // The hero and photo story are not nav sections; while they cross the middle of the screen nothing is highlighted.
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) setActiveSection(SECTIONS.some(section => section.id === entry.target.id) ? entry.target.id : '')
            })
        }, {rootMargin: '-45% 0px -50% 0px'})
        document.querySelectorAll('.sfg-hero, .sfg-story, .sfg-alpine-log, #sfg-board, #sfg-ceremony, #sfg-flyers, #sfg-services')
            .forEach(element => observer.observe(element))
        return () => observer.disconnect()
    }, [design])

    useEffect(() => {
        if (!toast) return
        const timer = window.setTimeout(() => setToast(''), 3500)
        return () => window.clearTimeout(timer)
    }, [toast])

    const visibleItems = useMemo(() => {
        const search = query.trim().toLowerCase()
        return intelItems
            .filter(item => filter === 'ALL' || item.category === filter)
            .filter(item => !search || `${item.title} ${item.summary} ${item.details.join(' ')}`.toLowerCase().includes(search))
            .sort((a, b) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] || b.date.localeCompare(a.date))
    }, [filter, query])

    const today = now.toDateString()
    const upcoming = useMemo(() => {
        const start = new Date(today)
        return intelItems
            .filter(item => item.category !== 'DOCUMENT' && item.category !== 'ANNOUNCEMENT' && toLocalDate(item.date) >= start)
            .sort((a, b) => a.date.localeCompare(b.date))
            .slice(0, 3)
    }, [today])

    const openOnBoard = (id: string) => {
        setFilter('ALL')
        setQuery('')
        setOpenId(id)
        window.requestAnimationFrame(() => {
            document.getElementById(`sfg-intel-${id}`)?.closest('li')?.scrollIntoView({behavior: 'smooth', block: 'center'})
        })
    }

    const countFor = (category: 'ALL' | Category) =>
        category === 'ALL' ? intelItems.length : intelItems.filter(item => item.category === category).length

    const scrollTo = (id: string) => {
        document.getElementById(id)?.scrollIntoView({behavior: 'smooth', block: 'start'})
    }

    const closeFlyer = React.useCallback(() => setActiveFlyer(null), [])

    const nextUp = (
        <aside className={'sfg-nextup'} aria-label={'Next up'}>
            <p className={'sfg-panel-label'}>Next up</p>
            <ol>
                {upcoming.map(item => (
                    <li key={item.id}>
                        <button className={'sfg-nextup-item'} onClick={() => openOnBoard(item.id)}>
                            <span className={'sfg-nextup-date'}>
                                <span>{formatShortDate(item.date).slice(0, 2)}</span>
                                {formatShortDate(item.date).slice(3, 6)}
                            </span>
                            <span className={'sfg-nextup-text'}>
                                <span className={'sfg-nextup-title'}>{item.title}</span>
                                <span className={'sfg-nextup-meta'}>{item.category} · {daysUntil(item.date, now)}</span>
                            </span>
                            <span className={'sfg-nextup-go'} aria-hidden={true}>→</span>
                        </button>
                    </li>
                ))}
            </ol>
        </aside>
    )

    return (
        <MotionConfig reducedMotion={'user'}>
            <div className={'sfg-page'} data-design={design}>
                <p className={'sfg-demo-banner'}>Concept demo by HITL Engineering · Sample content · Not an official U.S. Army website</p>

                <header className={'sfg-nav'}>
                    <div className={'sfg-nav-inner'}>
                        <button className={'sfg-brand'} onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
                            <span className={'sfg-brand-mark'} aria-hidden={true}/>
                            {isTactical ? '1SFG(A)' : 'First Group'}
                        </button>
                        <nav className={'sfg-nav-links'} aria-label={'Page sections'}>
                            {SECTIONS.map(section => (
                                <button
                                    key={section.id}
                                    className={`sfg-nav-link ${activeSection === section.id ? 'active' : ''}`}
                                    onClick={() => scrollTo(section.id)}
                                >
                                    {section.label}
                                </button>
                            ))}
                        </nav>
                        <button className={'sfg-nav-back'} onClick={() => setPageSelector(0)}>
                            <span aria-hidden={true}>←</span> 75th RGR
                        </button>
                    </div>
                    {isAlpine && <AlpineAltimeter/>}
                </header>

                {isAlpine ?
                <AlpineHero now={now} nextUp={nextUp} onOpenBoard={() => scrollTo('sfg-board')} onOpenCeremony={() => scrollTo('sfg-ceremony')}/>
                :
                <section className={'sfg-hero'}>
                    <div className={'sfg-grid-bg'} aria-hidden={true}/>
                    <div className={'sfg-hero-frame'}>
                        <span className={'sfg-corner tl'} aria-hidden={true}/>
                        <span className={'sfg-corner tr'} aria-hidden={true}/>
                        <span className={'sfg-corner bl'} aria-hidden={true}/>
                        <span className={'sfg-corner br'} aria-hidden={true}/>

                        <div className={'sfg-hero-grid'}>
                            <div>
                                <p className={'sfg-eyebrow'}>
                                    <span className={'sfg-live-dot'} aria-hidden={true}/>
                                    {isTactical ? 'Group operations board · Live' : 'The First Group Journal · Vol. I'}
                                </p>
                                <h1 className={'sfg-hero-title'}>
                                    <span className={'sfg-hero-unit'}>1st Special Forces Group (Airborne)</span>
                                    First Group
                                </h1>
                                <p className={'sfg-motto'}>
                                    {isTactical ? <Typewriter text={'DE OPPRESSO LIBER'}/> : <>De Oppresso Liber <span>· to free the oppressed</span></>}
                                </p>
                                <p className={'sfg-hero-copy'}>
                                    Announcements, ceremonies, flyers and events for the Group and its families. One place, always current.
                                </p>
                                <div className={'sfg-hero-actions'}>
                                    <button className={'sfg-btn sfg-btn-primary'} onClick={() => scrollTo('sfg-board')}>Open the board</button>
                                    <button className={'sfg-btn sfg-btn-ghost'} onClick={() => scrollTo('sfg-ceremony')}>Ceremony invite</button>
                                </div>
                            </div>

                            {isEditorial &&
                                <figure className={'sfg-hero-photo'}>
                                    <img src={photos.hero.src} alt={photos.hero.alt}/>
                                    <figcaption>Training, Washington State. Photos are placeholders for the Group's own.</figcaption>
                                </figure>
                            }

                            {nextUp}
                        </div>

                        {isTactical ?
                        <dl className={'sfg-telemetry'}>
                            <div>
                                <dt>DTG</dt>
                                <dd>{formatDTG(now)}</dd>
                            </div>
                            <div>
                                <dt>Zulu</dt>
                                <dd>{pad(now.getUTCHours())}:{pad(now.getUTCMinutes())}:{pad(now.getUTCSeconds())}</dd>
                            </div>
                            <div>
                                <dt>AO</dt>
                                <dd>JBLM · Okinawa</dd>
                            </div>
                            <div>
                                <dt>Grid</dt>
                                <dd>47.09N 122.58W</dd>
                            </div>
                        </dl>
                        :
                        <p className={'sfg-dateline'}>
                            {now.toLocaleDateString('en-US', {weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'})}
                            <span aria-hidden={true}> · </span>Joint Base Lewis-McChord, Washington
                            <span aria-hidden={true}> · </span>Torii Station, Okinawa
                        </p>
                        }
                    </div>
                </section>
                }

                {isEditorial && <FirstSFGPhotoStory/>}
                {isAlpine && <AlpineFieldLog/>}

                <motion.section id={'sfg-board'} className={'sfg-section'} {...reveal}>
                    <div className={'sfg-section-head'}>
                        <p className={'sfg-section-code'}>{sectionCode(design, '01', isTactical ? 'Intel board' : 'The board')}</p>
                        <h2>Announcements and events</h2>
                        <p className={'sfg-section-copy'}>Filter by type, search everything, and tap a line to read the full message.</p>
                    </div>

                    <div className={'sfg-board-controls'}>
                        <div className={'sfg-filters'} role={'group'} aria-label={'Filter by type'}>
                            {FILTERS.map(option => (
                                <button
                                    key={option}
                                    className={`sfg-chip ${filter === option ? 'active' : ''}`}
                                    aria-pressed={filter === option}
                                    onClick={() => setFilter(option)}
                                >
                                    {option}<span className={'sfg-chip-count'}>{countFor(option)}</span>
                                </button>
                            ))}
                        </div>
                        <label className={'sfg-search'}>
                            <span className={'sfg-visually-hidden'}>Search the board</span>
                            <span className={'sfg-search-prompt'} aria-hidden={true}>{isTactical ? '>_' : <Search size={16}/>}</span>
                            <input
                                type={'search'}
                                value={query}
                                onChange={event => setQuery(event.target.value)}
                                placeholder={'Search the board'}
                            />
                            {query &&
                                <button className={'sfg-search-clear'} onClick={() => setQuery('')} aria-label={'Clear search'}>✕</button>
                            }
                        </label>
                    </div>

                    {visibleItems.length > 0 ?
                        <ul className={'sfg-intel-list'}>
                            {visibleItems.map(item => (
                                <IntelCard
                                    key={item.id}
                                    item={item}
                                    isOpen={openId === item.id}
                                    onToggle={() => setOpenId(openId === item.id ? null : item.id)}
                                    onDownload={() => setToast('Sample document. Your unit\'s PDF goes here.')}
                                />
                            ))}
                        </ul>
                        :
                        <div className={'sfg-empty'}>
                            <p>{isTactical ? 'No traffic matches' : 'Nothing on the board matches'} "{query}".</p>
                            <button className={'sfg-btn sfg-btn-ghost'} onClick={() => {setQuery(''); setFilter('ALL')}}>Clear search</button>
                        </div>
                    }
                </motion.section>

                <motion.section id={'sfg-ceremony'} className={'sfg-section'} {...reveal}>
                    <div className={'sfg-section-head'}>
                        <p className={'sfg-section-code'}>{sectionCode(design, '02', 'Ceremony')}</p>
                        <h2>You are invited</h2>
                        <p className={'sfg-section-copy'}>A digital invitation guests can RSVP to, save to their calendar and pull up at the gate.</p>
                    </div>
                    <FirstSFGCeremony onNotify={setToast}/>
                </motion.section>

                <motion.section id={'sfg-flyers'} className={'sfg-section'} {...reveal}>
                    <div className={'sfg-section-head'}>
                        <p className={'sfg-section-code'}>{sectionCode(design, '03', 'Flyers')}</p>
                        <h2>Flyers and posters</h2>
                        <p className={'sfg-section-copy'}>Tap a flyer to open it full size.</p>
                    </div>
                    <div className={'sfg-flyer-grid'}>
                        {flyers.map(flyer => (
                            <button key={flyer.id} className={'sfg-flyer'} onClick={() => setActiveFlyer(flyer)} aria-label={`Open ${flyer.title} flyer`}>
                                <FlyerPoster flyer={flyer} design={design}/>
                                <span className={'sfg-flyer-open'} aria-hidden={true}>View ↗</span>
                            </button>
                        ))}
                    </div>
                </motion.section>

                <motion.section id={'sfg-services'} className={'sfg-section sfg-section-services'} {...reveal}>
                    <FirstSFGShowcase design={design} onBriefBarChange={setIsBriefBarVisible}/>
                </motion.section>

                <footer className={'sfg-footer'}>
                    <p>1st Special Forces Group (Airborne) · Joint Base Lewis-McChord, WA</p>
                    <p>Concept page designed by HITL Engineering LLC, a registered Washington State LLC. All names, dates and documents are sample content.</p>
                    <button className={'sfg-btn sfg-btn-ghost'} onClick={() => setPageSelector(0)}>Back to 75th Ranger Regiment</button>
                </footer>

                {activeFlyer && <FlyerModal flyer={activeFlyer} onClose={closeFlyer} design={design}/>}
                <FirstSFGDesignSwitcher design={design} onSwitch={switchDesign} raised={isBriefBarVisible}/>
                <div className={`sfg-toast ${toast ? 'show' : ''}`} role={'status'} aria-live={'polite'}>{toast}</div>
            </div>
        </MotionConfig>
    )
}
export default FirstSFG
